import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import localeData from "dayjs/plugin/localeData"
import localizedFormat from "dayjs/plugin/localizedFormat"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"

import { EventType } from "@/utils/events"

import { eventBus } from "./EventBus"

// 扩展 dayjs 功能
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)
dayjs.extend(localeData)
dayjs.extend(advancedFormat)

// 常用日期格式
const DateFormats = {
  DATE: "YYYY-MM-DD",
  // DATE_TIME: "YYYY-MM-DD HH:mm:ss",
  // DATE_TIME_AM_PM: "YYYY-MM-DD hh:mm a",
  // MONTH_DAY: "MMM DD",
  MONTH_DAY_YEAR: "MMM DD, YYYY",
  MONTH_DAY_TIME: "MMM DD, hh:mm a",
  LONG_MONTH_DAY_YEAR: "MMMM DD, YYYY",
  // DAY_MONTH_YEAR: "DD MMM YYYY",
  // TIME: "HH:mm:ss",
  // TIME_AM_PM: "hh:mm a",
  // WEEKDAY_MONTH_DAY_YEAR: "dddd, MMMM D, YYYY",
  // WEEKDAY_MONTH_DAY_YEAR_TIME: "dddd, MMMM D, YYYY h:mm a",
}

// 支持的语言映射
type SupportedLocales = {
  [key: string]: () => Promise<any>;
};

const SUPPORTED_LOCALES: SupportedLocales = {
  zh: () => import("dayjs/locale/zh"),
  "zh-hk": () => import("dayjs/locale/zh-hk"),
  cs: () => import("dayjs/locale/cs"),
  da: () => import("dayjs/locale/da"),
  de: () => import("dayjs/locale/de"),
  es: () => import("dayjs/locale/es"),
  fi: () => import("dayjs/locale/fi"),
  fr: () => import("dayjs/locale/fr"),
  it: () => import("dayjs/locale/it"),
  ja: () => import("dayjs/locale/ja"),
  ko: () => import("dayjs/locale/ko"),
  nl: () => import("dayjs/locale/nl"),
  pl: () => import("dayjs/locale/pl"),
  pt: () => import("dayjs/locale/pt"),
  "pt-br": () => import("dayjs/locale/pt-br"),
  sv: () => import("dayjs/locale/sv"),
  th: () => import("dayjs/locale/th"),
  tr: () => import("dayjs/locale/tr"),
}

// 获取当前用户的时区
const getUserTimezone = (): string => {
  return dayjs.tz.guess()
}

// 设置默认时区
const setDefaultTimezone = (timezone: string = getUserTimezone()): void => {
  dayjs.tz.setDefault(timezone)
}

// 设置语言
const setLocale = async (locale: string): Promise<void> => {
  const normalizedLocale = locale.toLowerCase()

  // 检查是否支持该语言
  if (normalizedLocale in SUPPORTED_LOCALES) {
    // 动态加载语言包
    await SUPPORTED_LOCALES[normalizedLocale]()
    dayjs.locale(normalizedLocale)
  } else {
    // 默认使用英语
    dayjs.locale("en")
    console.warn(`Locale ${locale} is not supported, falling back to English`)
  }

  eventBus.emit(EventType.DAYJS_LOCALE_LOADED, { locale: normalizedLocale })
}

// 获取当前语言
const getLocale = (): string => {
  return dayjs.locale()
}

// 创建一个带时区的日期对象
const createDate = (date?: dayjs.ConfigType): dayjs.Dayjs => {
  // 如果输入是ISO8601格式的字符串且没有时区信息，则直接在应用时区下解析
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(date)) {
    return dayjs.tz(date, getUserTimezone())
  }
  // 其他情况按照正常方式处理
  return dayjs(date).tz()
}

// 格式化日期
const formatDate = (
  date: dayjs.ConfigType,
  format: string = DateFormats.DATE,
): string => {
  return createDate(date).format(format)
}

// 从Unix时间戳创建日期
const fromUnixTimestamp = (timestamp: number): dayjs.Dayjs => {
  return dayjs.unix(timestamp).tz()
}

/**
 * 校验是否符合某个日期格式规格
 * @param format  例如： YYYY-MM-DD
 * @param datetime  需要校验的时间
 */
const checkDatetimeFormat = (format: string = "YYYY-MM-DD", datetime: string): (dayjs.Dayjs | null) => {
  const s = dayjs(datetime)

  if (s.format(format) === datetime) {
    return s
  }
  return null
}

export {
  checkDatetimeFormat,
  createDate,
  DateFormats,
  formatDate,
  fromUnixTimestamp,
  getLocale,
  getUserTimezone,
  setDefaultTimezone,
  setLocale,
}
